import { useEffect, useState } from 'react';
import { CssBaseline, ThemeProvider, createTheme } from '@mui/material';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { getToken } from './api';
import { LoginPage } from './LoginPage';
import { DevicesPage } from './DevicesPage';

const theme = createTheme({
  palette: { mode: 'light', background: { default: '#f5f6f8' } },
  typography: { fontFamily: 'Inter, system-ui, sans-serif' },
});

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, refetchOnWindowFocus: true } },
});

export function App() {
  const [token, setTokenState] = useState<string | null>(getToken);
  const [mustChangePassword, setMustChangePassword] = useState(false);

  // A file dropped anywhere OUTSIDE a real drop zone must do nothing.
  // Without this the browser's default takes the drop and navigates to the
  // file: on a page the operator meant to send a file FROM, Chrome answers by
  // downloading it back into their own Downloads folder — which reads exactly
  // like the transfer ran backwards (reported live, 2026-08-03).
  // The viewer's own handlers stop propagation-free: they call preventDefault
  // first, and these listeners are passive observers of what escaped.
  useEffect(() => {
    const swallow = (event: DragEvent) => {
      // A real drop zone (the screen viewer) has already prevented the default
      // by the time this bubbles to window — leave those alone.
      if (event.defaultPrevented) return;
      if (!Array.from(event.dataTransfer?.types ?? []).includes('Files')) return;
      event.preventDefault();
    };
    window.addEventListener('dragover', swallow);
    window.addEventListener('drop', swallow);
    return () => {
      window.removeEventListener('dragover', swallow);
      window.removeEventListener('drop', swallow);
    };
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <QueryClientProvider client={queryClient}>
        {token ? (
          <DevicesPage
            forcePasswordChange={mustChangePassword}
            onLogout={() => {
              queryClient.clear();
              setMustChangePassword(false);
              setTokenState(null);
            }}
          />
        ) : (
          <LoginPage
            onLoggedIn={(mustChange) => {
              setMustChangePassword(mustChange);
              setTokenState(getToken());
            }}
          />
        )}
      </QueryClientProvider>
    </ThemeProvider>
  );
}
